<script setup lang="ts">
import { computed } from 'vue'
import {
  getFairIconDefinition,
  resolveFairIconName,
  type FairIconName,
  type FairIconVariant,
} from '@/ui/tokens/icons.data'
import { getMainIconSvg } from './fairIconAssets'
import { prepareIconSvg } from './iconSvg'

interface FairIconProps {
  name: FairIconName | string
  size?: number
  color?: string
  variant?: FairIconVariant
}

const props = withDefaults(defineProps<FairIconProps>(), {
  variant: 'default',
})

const resolvedName = computed(() => resolveFairIconName(props.name))

const definition = computed(() => {
  if (!resolvedName.value) {
    return undefined
  }

  return getFairIconDefinition(resolvedName.value)
})

const svgContent = computed(() => {
  if (!resolvedName.value) {
    return ''
  }

  const raw = getMainIconSvg(resolvedName.value)
  if (!raw) {
    return ''
  }

  const useBrand = Boolean(definition.value?.brand && props.variant === 'brand')
  return prepareIconSvg(raw, useBrand ? 'brand' : 'monochrome')
})

const iconSize = computed(() => props.size ?? definition.value?.defaultSize ?? 24)

const iconColor = computed(() => {
  if (definition.value?.brand && props.variant === 'brand') {
    return undefined
  }

  return props.color ?? 'currentColor'
})

const rootClass = computed(() => {
  const name = resolvedName.value
  return name ? ['fair-icon', `fair-icon--${name}`] : 'fair-icon'
})
</script>

<template>
  <span
    v-if="svgContent"
    :class="rootClass"
    :style="{
      width: `${iconSize}px`,
      height: `${iconSize}px`,
      color: iconColor,
    }"
    aria-hidden="true"
    v-html="svgContent"
  />
</template>

<style scoped>
.fair-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  line-height: 0;
}

.fair-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}

/*
  DS Icon/Arrow* & caret: frame = `size`, glyph is inset (Figma ~17% / 30%).
  Without this the tight SVG viewBox fills the whole frame and looks oversized.
*/
.fair-icon--arrow-left :deep(svg),
.fair-icon--double-arrow-left :deep(svg),
.fair-icon--double-arrow-right :deep(svg),
.fair-icon--caret-left :deep(svg),
.fair-icon--caret-right :deep(svg) {
  width: 37.5%;
  height: 65.75%;
}

.fair-icon--arrow-left-small :deep(svg),
.fair-icon--arrow-right-small :deep(svg) {
  width: 84%;
  height: 59%;
}

/*
  Chevron / caret ink mass sits on the open side of the bbox, so geometric
  centering looks tip-heavy empty. Nudge toward the tip for optical center.
*/
.fair-icon--arrow-left :deep(svg),
.fair-icon--double-arrow-left :deep(svg),
.fair-icon--caret-left :deep(svg) {
  transform: translateX(-8%);
}

.fair-icon--double-arrow-right :deep(svg),
.fair-icon--caret-right :deep(svg) {
  transform: translateX(8%);
}

.fair-icon--arrow-left-small :deep(svg) {
  transform: translateX(-4%);
}

.fair-icon--arrow-right-small :deep(svg) {
  transform: translateX(4%);
}
</style>
